/**
 * The OI's single status (ENG-213): the RTM authority's `rtm_state`, read
 * straight off `v1/output/aircraft/<TAIL>/oi/declaration`. Nothing is inferred
 * on the frontend — no schedule guess, no geometric conformance.
 *
 * `Scheduled` (ENG-313) is the one frontend-assigned value: an Accepted
 * declaration read from Blender that no aircraft has linked to telemetry yet.
 */
export const RTM_STATUSES = [
  'Scheduled',
  'Accepted',
  'Active',
  'Non-Conforming',
  'Contingent',
  'Ended',
  'Rejected',
  'Unknown',
] as const;
export type RtmStatus = (typeof RTM_STATUSES)[number];

/** Published enum, decoded by the backend team (ENG-213 comment, 2026-09-26). */
const BY_CODE: Record<number, Exclude<RtmStatus, 'Unknown' | 'Scheduled'>> = {
  1: 'Accepted',
  2: 'Active',
  3: 'Non-Conforming',
  4: 'Contingent',
  5: 'Ended',
  8: 'Rejected',
};

/** `Unknown` when the backend hasn't published a state, or sends a code
 *  outside the enum — never a guess. */
export function rtmStatusFromCode(code: number | null | undefined): RtmStatus {
  if (code == null) return 'Unknown';
  return BY_CODE[code] ?? 'Unknown';
}

export type RtmStatusTone = 'ok' | 'info' | 'warn' | 'crit' | 'muted';

const TONE: Record<RtmStatus, RtmStatusTone> = {
  Scheduled: 'info',
  Accepted: 'info',
  Active: 'ok',
  'Non-Conforming': 'crit',
  Contingent: 'warn',
  Ended: 'muted',
  Rejected: 'crit',
  Unknown: 'muted',
};

/** Badge variant for a status — the same keys as `components/ui/badge`. */
export function rtmStatusTone(status: RtmStatus): RtmStatusTone {
  return TONE[status];
}
